import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, Clock, User } from "lucide-react";
import { getInsight, INSIGHTS } from "../data/insights";

const fmtDate = (s) => new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

const InsightDetail = () => {
  const { slug } = useParams();
  const insight = getInsight(slug);
  if (!insight) return <Navigate to="/insights" replace />;

  const related = INSIGHTS.filter(i => i.slug !== slug && i.category === insight.category).slice(0, 3);

  return (
    <div data-testid="insight-detail-page">
      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-16">
        <div className="absolute inset-0 corporate-grid opacity-50 pointer-events-none" />
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-brand-green/10 blur-3xl float-slow pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10">
          <Link to="/insights" data-testid="insight-back" className="inline-flex items-center gap-2 text-sm text-brand-slate hover:text-brand-green transition-colors mb-8">
            <ArrowLeft className="h-4 w-4" /> All insights
          </Link>
          <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand-gold mb-4">{insight.category}</div>
          <h1 className="font-serif text-4xl md:text-6xl text-brand-ink tracking-tight leading-[1.05]">{insight.title}</h1>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-brand-slate">
            <span className="inline-flex items-center gap-1.5"><User className="h-4 w-4" /> {insight.author}</span>
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {fmtDate(insight.date)}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> {insight.readTime} min read</span>
          </div>
        </div>
      </section>

      {/* Cover */}
      <section>
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="aspect-[16/8] rounded-sm overflow-hidden">
            <img src={insight.cover} alt={insight.title} className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <p className="font-serif italic text-xl md:text-2xl text-brand-ink leading-snug border-l-4 border-brand-green pl-6 mb-12">{insight.excerpt}</p>
          <div className="space-y-6 text-brand-ink text-lg leading-relaxed">
            {insight.content.map((p, i) => <p key={i}>{p}</p>)}
          </div>

          {/* CTA */}
          <div className="mt-16 bg-brand-ink text-white rounded-sm p-10 relative overflow-hidden">
            <div className="absolute -bottom-16 -right-16 w-56 h-56 rounded-full bg-brand-green/30 blur-3xl" />
            <div className="relative">
              <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand-gold mb-3">Take the next step</div>
              <h3 className="font-serif text-2xl md:text-3xl tracking-tight leading-tight">Want a tailored point of view for your organization?</h3>
              <p className="mt-3 text-white/70">A senior consultant will respond within one business day.</p>
              <Link to="/contact" data-testid="insight-contact-cta" className="inline-flex items-center gap-2 mt-6 bg-brand-gold text-brand-ink px-6 py-3 rounded-sm font-semibold hover:bg-white transition-colors">
                Book a Consultation <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-20 bg-brand-green-50/40 border-t border-black/5">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand-gold mb-4">Related perspectives</div>
            <h3 className="font-serif text-3xl md:text-4xl text-brand-ink tracking-tight mb-10">More from {insight.category}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map(r => (
                <Link key={r.slug} to={`/insights/${r.slug}`} data-testid={`related-${r.slug}`} className="group bg-white border border-black/5 rounded-sm overflow-hidden hover:shadow-card-hover hover:-translate-y-1 transition-all">
                  <div className="aspect-[5/3] relative overflow-hidden">
                    <img src={r.cover} alt={r.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-6">
                    <div className="text-xs text-brand-slate">{fmtDate(r.date)} · {r.readTime} min</div>
                    <h4 className="font-serif text-xl text-brand-ink mt-2 leading-snug group-hover:text-brand-green transition-colors">{r.title}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default InsightDetail;
